import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangle, Check, ChevronLeft, X } from 'lucide-react';
import { REQUEST_STATUSES, type RequestStatus } from '@geoic/shared';
import { Button } from '@/components/ui/Button.js';
import { Input, Select, Textarea } from '@/components/ui/Field.js';
import { RequestStatusBadge } from '@/components/ui/Badge.js';
import { useToast } from '@/components/ui/Toast.js';
import { dateTime } from '@/lib/format.js';
import { errorMessage } from '@/lib/api-error.js';
import { cn } from '@/lib/cn.js';
import { queryKeys } from '@/lib/query-keys.js';
import { initials } from '@/components/layout/Topbar.js';
import { RequestPayloadView } from './RequestPayloadView.js';
import { TYPE_DOTS } from './RequestsPage.js';
import { REQUEST_STATUS_LABELS, REQUEST_TYPE_LABELS, requestsApi } from './requests-api.js';

/**
 * Ariza kartochkasi — MAKETDAN (`D2`, MAHSULOT ARIZASI va DRON-SHOU
 * BRIFI).
 *
 * Ikki ustun: chapda mijoz, ariza tarkibi, texnik ma'lumot va izohlar
 * tarixi; o'ngda YOPISHGAN «Ariza boshqaruvi» hamda Telegram holati.
 *
 * Tarkib qismi turga qarab o'zgaradi (`RequestPayloadView`), qolgan
 * hamma narsa BIR XIL — maketda ikkala ekran ham shu karkasda.
 */
export function RequestDetailPage() {
  const { id = '' } = useParams();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [note, setNote] = useState('');
  const [amount, setAmount] = useState<string | null>(null);
  const [assignee, setAssignee] = useState<string | null>(null);
  const [techOpen, setTechOpen] = useState(false);

  const request = useQuery({
    queryKey: queryKeys.requests.one(id),
    queryFn: () => requestsApi.one(id),
  });

  async function refresh(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: queryKeys.requests.all });
  }

  const update = useMutation({
    mutationFn: (body: Parameters<typeof requestsApi.update>[1]) => requestsApi.update(id, body),
    onSuccess: async () => {
      toast.success('Saqlandi');
      await refresh();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const addNote = useMutation({
    mutationFn: (text: string) => requestsApi.addNote(id, text),
    onSuccess: async () => {
      setNote('');
      await refresh();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const data = request.data;

  const back = (
    <Link
      to="/requests"
      className="flex items-center gap-2 self-start text-[13.5px] font-semibold text-brand-600"
    >
      <ChevronLeft className="size-[15px]" aria-hidden />
      Arizalar ro‘yxati
    </Link>
  );

  if (!data) {
    return (
      <div className="flex flex-col gap-[18px]">
        {back}
        <p className="text-sm text-n-600">
          {request.isLoading ? 'Yuklanmoqda…' : 'Ariza topilmadi.'}
        </p>
      </div>
    );
  }

  const clientFields: Array<{ label: string; value: string }> = [
    { label: 'Ism', value: data.contact.name },
    { label: 'Telefon', value: data.contact.phone },
    { label: 'Elektron pochta', value: data.contact.email ?? '—' },
    { label: 'Tashkilot', value: data.contact.company ?? '—' },
    {
      label: 'Qulay aloqa',
      value: PREFERRED_LABELS[data.contact.preferredContact ?? 'any'] ?? '—',
    },
  ];

  const techFields: Array<{ label: string; value: string }> = [
    { label: 'Sahifa', value: data.sourceUrl || '—' },
    { label: 'Til', value: data.locale },
    { label: 'IP', value: data.ip || '—' },
    { label: 'reCAPTCHA', value: data.recaptchaScore === null ? '—' : String(data.recaptchaScore) },
    { label: 'Yaratildi', value: dateTime(data.createdAt) },
    { label: 'Yangilandi', value: dateTime(data.updatedAt) },
  ];

  return (
    <div className="flex flex-col gap-[18px]">
      {back}

      <div className="flex flex-wrap items-start justify-between gap-6 border-b-2 border-n-900 pb-4">
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-3.5">
            <h1 className="font-mono text-[26px] font-semibold tracking-[-0.02em] text-n-900">
              {data.requestNumber}
            </h1>
            <RequestStatusBadge status={data.status} />
            <span className="inline-flex items-center gap-2 text-[13px] text-n-800">
              <span aria-hidden className={cn('size-[7px]', TYPE_DOTS[data.type])} />
              {REQUEST_TYPE_LABELS[data.type]}
            </span>
          </div>
          <span className="text-[13.5px] tabular text-n-600">
            Kelib tushdi: {dateTime(data.createdAt)}
            {data.sourceUrl && ` · manba: ${data.sourceUrl}`}
          </span>
        </div>

        <Button
          variant="outline"
          className="border-error-600 text-error-600 hover:bg-error-600 hover:text-white"
          disabled={data.status === 'spam'}
          onClick={() => update.mutate({ status: 'spam' })}
        >
          Spam
        </Button>
      </div>

      <div className="grid items-start gap-5 xl:grid-cols-[1fr_348px]">
        <div className="flex flex-col gap-4">
          <Panel title="Mijoz">
            <div className="grid gap-[18px] p-5 sm:grid-cols-2">
              {clientFields.map((field) => (
                <div key={field.label} className="flex flex-col gap-1">
                  <span className="font-mono text-[11px] tracking-[0.1em] text-n-600 uppercase">
                    {field.label}
                  </span>
                  <span className="text-[15px] font-medium text-n-900">{field.value}</span>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Ariza tarkibi">
            <div className="p-5">
              <RequestPayloadView payload={data.payload} />
              {data.comment && (
                <p className="mt-4 border-t border-n-100 pt-4 text-sm whitespace-pre-wrap text-n-800">
                  {data.comment}
                </p>
              )}
            </div>
          </Panel>

          {/* Texnik ma'lumot — maketda YIG'ILGAN holatda ochiladi */}
          <section className="border border-n-200 bg-white">
            <button
              type="button"
              onClick={() => setTechOpen((value) => !value)}
              aria-expanded={techOpen}
              className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left hover:bg-n-50"
            >
              <span className="text-[15px] font-semibold text-n-900">Texnik ma’lumot</span>
              <span className="font-mono text-[17px] text-accent-600">{techOpen ? '−' : '+'}</span>
            </button>
            {techOpen && (
              <div className="grid gap-4 border-t border-n-100 px-5 pt-3.5 pb-5 sm:grid-cols-2 lg:grid-cols-3">
                {techFields.map((field) => (
                  <div key={field.label} className="flex flex-col gap-1">
                    <span className="font-mono text-[11px] tracking-[0.1em] text-n-600 uppercase">
                      {field.label}
                    </span>
                    <span className="font-mono text-[13.5px] break-all text-n-900">
                      {field.value}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>

          <Panel title="Izohlar tarixi">
            <div>
              {data.notes.map((item, index) => (
                <div key={index} className="flex gap-3.5 border-b border-n-100 p-5">
                  <span className="flex size-8 shrink-0 items-center justify-center bg-brand-600 text-xs font-bold text-white">
                    {initials(item.userName ?? '—')}
                  </span>
                  <div className="flex min-w-0 flex-col gap-1.5">
                    <div className="flex items-baseline gap-2.5">
                      <span className="text-[13.5px] font-semibold text-n-900">
                        {item.userName ?? 'Xodim'}
                      </span>
                      <span className="font-mono text-xs tabular text-n-600">
                        {dateTime(item.createdAt)}
                      </span>
                    </div>
                    <span className="text-sm leading-relaxed whitespace-pre-wrap text-n-800">
                      {item.text}
                    </span>
                  </div>
                </div>
              ))}

              <div className="flex flex-col gap-3 p-5 sm:flex-row sm:items-start">
                <div className="flex-1">
                  <Textarea
                    rows={2}
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    placeholder="Izoh qo‘shish — faqat xodimlar ko‘radi"
                  />
                </div>
                <Button
                  loading={addNote.isPending}
                  disabled={note.trim() === ''}
                  onClick={() => addNote.mutate(note.trim())}
                >
                  Qo‘shish
                </Button>
              </div>
            </div>
          </Panel>
        </div>

        <div className="flex flex-col gap-4 xl:sticky xl:top-4">
          {/* Boshqaruv qutisi maketda QORA chegarali — u asosiy harakat */}
          <section className="border border-n-900 bg-white">
            <div className="border-b border-n-200 px-[18px] py-4 text-[15px] font-semibold text-n-900">
              Ariza boshqaruvi
            </div>
            <div className="flex flex-col gap-4 p-[18px]">
              <div className="flex flex-col gap-2">
                <span className="text-[12.5px] font-semibold text-n-800">Holat</span>
                <div className="grid grid-cols-2 gap-1.5">
                  {REQUEST_STATUSES.map((value) => (
                    <button
                      key={value}
                      type="button"
                      aria-pressed={data.status === value}
                      onClick={() => update.mutate({ status: value as RequestStatus })}
                      className={cn(
                        'border px-1.5 py-2.5 text-[12.5px] font-semibold',
                        data.status === value
                          ? 'border-brand-600 bg-brand-600 text-white'
                          : 'border-n-200 bg-white text-n-800 hover:border-brand-600',
                      )}
                    >
                      {REQUEST_STATUS_LABELS[value]}
                    </button>
                  ))}
                </div>
              </div>

              <Select
                label="Mas’ul xodim"
                value={assignee ?? data.assignedTo ?? ''}
                placeholder="Biriktirilmagan"
                options={[]}
                onChange={(event) => setAssignee(event.target.value)}
                hint="Xodimlar ro‘yxati «Foydalanuvchilar» bo‘limidan olinadi"
              />

              <Input
                type="number"
                min={0}
                label="Taxminiy summa (so‘m)"
                className="font-mono tabular"
                value={amount ?? data.estimatedAmount ?? ''}
                onChange={(event) => setAmount(event.target.value)}
              />

              <Button
                variant="secondary"
                loading={update.isPending}
                onClick={() =>
                  update.mutate({
                    estimatedAmount: amount === null || amount === '' ? null : Number(amount),
                    ...(assignee !== null ? { assignedTo: assignee || null } : {}),
                  })
                }
              >
                Saqlash
              </Button>
            </div>
          </section>

          <Panel title="Telegram">
            <div className="flex flex-col gap-3 p-[18px]">
              {data.telegramError ? (
                <div className="flex items-start gap-2.5 border border-error-600 bg-error-50 p-3">
                  <X className="mt-0.5 size-4 shrink-0 text-error-600" aria-hidden />
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[13.5px] font-semibold text-error-600">Yuborilmadi</span>
                    <span className="font-mono text-xs break-all text-n-800">
                      {data.telegramError}
                    </span>
                  </div>
                </div>
              ) : data.telegramSentAt ? (
                <div className="flex items-start gap-2.5 border border-success-200 bg-success-50 p-3">
                  <Check className="mt-0.5 size-4 shrink-0 text-success-600" aria-hidden />
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[13.5px] font-semibold text-success-600">Yuborildi</span>
                    <span className="font-mono text-xs tabular text-n-800">
                      {dateTime(data.telegramSentAt)}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-2.5 border border-warning-200 bg-warning-50 p-3">
                  <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning-600" aria-hidden />
                  <span className="text-[13.5px] font-semibold text-warning-600">Yuborilmagan</span>
                </div>
              )}
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}

const PREFERRED_LABELS: Record<string, string> = {
  phone: 'Telefon qo‘ng‘irog‘i',
  telegram: 'Telegram',
  email: 'Elektron pochta',
  any: 'Farqi yo‘q',
};

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="border border-n-200 bg-white">
      <div className="border-b border-n-100 px-5 py-4 text-[15px] font-semibold text-n-900">
        {title}
      </div>
      {children}
    </section>
  );
}
